import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog.jsx";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MagnifyingGlass, Eye, ChatCircleDots, Copy, Check, Archive, PencilSimple, DownloadSimple, FilePdf, FileImage, FileArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { useRole } from "@/hooks/useRole";
import { FullPreviewModal } from "@/components/FullPreview/FullPreviewModal";

const STATUS_META = {
  draft: { label: "Draft", bg: "bg-[#F1F5F9]", text: "text-[#475569]" },
  saved: { label: "Saved", bg: "bg-[#E0F2FE]", text: "text-[#0369A1]" },
  approved: { label: "Approved", bg: "bg-[#DCFCE7]", text: "text-[#166534]" },
  archived: { label: "Archived", bg: "bg-[#FEE2E2]", text: "text-[#991B1B]" },
};

export default function TemplateGallery() {
  const api = useApi();
  const navigate = useNavigate();
  const { canManageMedia } = useRole();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [actioningId, setActioningId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (search.trim()) params.search = search.trim();
      const r = await api.get("/email-templates", { params });
      setTemplates(r.data || []);
    } catch (e) {
      toast.error("Could not load templates");
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, categoryFilter, search]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const categories = [...new Set(templates.map((t) => t.category).filter(Boolean))];

  const handleApprove = async (t) => {
    setActioningId(t.id);
    try {
      await api.post(`/email-templates/${t.id}/approve`);
      toast.success(`"${t.name}" approved`);
      fetchTemplates();
    } catch (e) {
      toast.error("Could not approve template");
    } finally {
      setActioningId(null);
    }
  };

  const handleArchive = async (t) => {
    setActioningId(t.id);
    try {
      await api.post(`/email-templates/${t.id}/archive`);
      toast.success(`"${t.name}" archived`);
      fetchTemplates();
    } catch (e) {
      toast.error("Could not archive template");
    } finally {
      setActioningId(null);
    }
  };

  const handleDuplicate = async (t) => {
    setActioningId(t.id);
    try {
      await api.post(`/email-templates/${t.id}/duplicate`);
      toast.success(`Duplicated "${t.name}"`);
      fetchTemplates();
    } catch (e) {
      toast.error("Could not duplicate template");
    } finally {
      setActioningId(null);
    }
  };

  const handleRenameSave = async () => {
    if (!renameValue.trim() || !renameTarget) return;
    setActioningId(renameTarget.id);
    try {
      await api.patch(`/email-templates/${renameTarget.id}`, { name: renameValue.trim() });
      toast.success("Renamed");
      setRenameTarget(null);
      fetchTemplates();
    } catch (e) {
      toast.error("Could not rename template");
    } finally {
      setActioningId(null);
    }
  };

  const openPreview = async (t) => {
    try {
      const r = await api.get(`/email-templates/${t.id}`);
      setPreview({ type: "newsletter", data: { html: r.data.html, subject: r.data.name } });
    } catch (e) {
      toast.error("Could not load template preview");
    }
  };

  const handleDownloadTemplate = async (t, format) => {
    try {
      const r = await api.get(`/email-templates/${t.id}/export.${format}`, { responseType: "blob" });
      const contentType = r.headers?.["content-type"];
      const cdMatch = (r.headers?.["content-disposition"] || "").match(/filename="([^"]+)"/);
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], contentType ? { type: contentType } : undefined));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = (cdMatch && cdMatch[1]) || `${t.name || "template"}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  const handleUseWithShee = (t) => {
    navigate("/creative-director", {
      state: { prefillMessage: `Use the saved template "${t.name}" (template id: ${t.id}) to draft a new newsletter.` },
    });
  };

  const handleRedesignWithShee = (t) => {
    navigate("/creative-director", {
      state: { prefillMessage: `Redesign the saved template "${t.name}" (template id: ${t.id}).` },
    });
  };

  return (
    <div data-testid="template-gallery">
      <p className="text-[#6B7C85] text-sm mb-4">
        Every newsletter design Shee creates — or that's saved from an approved send — lives here: reusable, versioned, and ready to hand back to Shee for a new send or a redesign.
      </p>

      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={18} weight="regular" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7C85]" />
          <Input
            data-testid="template-search-input"
            placeholder="Search templates by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-[#F8FAF9] border-[#E2E8F0] text-[#2D3139]"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="template-status-filter" className="w-[160px] bg-[#F8FAF9] border-[#E2E8F0]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="saved">Saved</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        {categories.length > 0 && (
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger data-testid="template-category-filter" className="w-[180px] bg-[#F8FAF9] border-[#E2E8F0]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center"><p className="text-[#6B7C85]">Loading…</p></div>
      ) : templates.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center" data-testid="template-gallery-empty">
          <p className="text-[#6B7C85]">No templates yet — ask Shee in the Creative Director chat to design one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {templates.map((t) => {
            const meta = STATUS_META[t.status] || STATUS_META.saved;
            const busy = actioningId === t.id;
            return (
              <div key={t.id} data-testid={`template-card-${t.id}`} className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden hover:shadow-lg hover:border-[#5FA8B4] transition-all duration-200 flex flex-col">
                <div className="aspect-[3/4] bg-slate-100 overflow-hidden relative cursor-pointer" onClick={() => openPreview(t)}>
                  <img src={t.thumbnail_url} loading="lazy" alt={t.name} className="w-full h-full object-cover object-top" data-testid={`template-thumbnail-${t.id}`} />
                  <span className={`absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full ${meta.bg} ${meta.text} text-[10px] font-semibold`} data-testid={`template-status-${t.id}`}>
                    {meta.label}
                  </span>
                  {t.version > 1 && (
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#2D3139] text-white text-[10px] font-semibold">
                      v{t.version}
                    </span>
                  )}
                </div>
                <div className="p-4 space-y-2 flex-1 flex flex-col">
                  <div>
                    <p className="text-sm font-semibold text-[#2D3139] truncate" title={t.name} data-testid={`template-name-${t.id}`}>{t.name}</p>
                    {t.category && <p className="text-[#6B7C85] text-xs mt-0.5">{t.category}</p>}
                  </div>
                  {t.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {t.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded-full bg-[#F0F7F8] text-[#3A7A8C] text-[10px] font-medium">{tag}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex-1" />
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <Button data-testid={`template-preview-${t.id}`} onClick={() => openPreview(t)} className="bg-white border border-[#5FA8B4] text-[#3A7A8C] hover:bg-[#F0F7F8] h-8 text-xs rounded-[10px] gap-1">
                      <Eye size={14} weight="regular" /> Preview
                    </Button>
                    <Button data-testid={`template-use-shee-${t.id}`} onClick={() => handleUseWithShee(t)} className="bg-[#5FA8B4] text-white hover:bg-[#3A7A8C] h-8 text-xs rounded-[10px] gap-1">
                      <ChatCircleDots size={14} weight="fill" /> Use with Shee
                    </Button>
                    {canManageMedia && (
                      <>
                        <Button data-testid={`template-redesign-${t.id}`} onClick={() => handleRedesignWithShee(t)} className="bg-white border border-[#E2E8F0] text-[#2D3139] hover:border-[#5FA8B4] h-8 text-xs rounded-[10px] gap-1">
                          <ChatCircleDots size={14} /> Redesign
                        </Button>
                        <Button data-testid={`template-duplicate-${t.id}`} disabled={busy} onClick={() => handleDuplicate(t)} className="bg-white border border-[#E2E8F0] text-[#2D3139] hover:border-[#5FA8B4] h-8 text-xs rounded-[10px] gap-1">
                          <Copy size={14} /> Duplicate
                        </Button>
                        <Button data-testid={`template-rename-${t.id}`} onClick={() => { setRenameTarget(t); setRenameValue(t.name); }} className="bg-white border border-[#E2E8F0] text-[#2D3139] hover:border-[#5FA8B4] h-8 text-xs rounded-[10px] gap-1">
                          <PencilSimple size={14} /> Rename
                        </Button>
                        {t.status !== "approved" && (
                          <Button data-testid={`template-approve-${t.id}`} disabled={busy} onClick={() => handleApprove(t)} className="bg-white border border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white h-8 text-xs rounded-[10px] gap-1">
                            <Check size={14} weight="bold" /> Approve
                          </Button>
                        )}
                        {t.status !== "archived" && (
                          <Button data-testid={`template-archive-${t.id}`} disabled={busy} onClick={() => handleArchive(t)} className="bg-white border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white h-8 text-xs rounded-[10px] gap-1">
                            <Archive size={14} /> Archive
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 pt-1">
                    <span className="text-[10px] text-[#6B7C85] mr-0.5">Download:</span>
                    <button data-testid={`template-download-html-${t.id}`} title="Download HTML" onClick={() => handleDownloadTemplate(t, "html")} className="text-[#6B7C85] hover:text-[#3A7A8C] p-1">
                      <FileArrowDown size={15} weight="regular" />
                    </button>
                    <button data-testid={`template-download-pdf-${t.id}`} title="Download PDF" onClick={() => handleDownloadTemplate(t, "pdf")} className="text-[#6B7C85] hover:text-[#3A7A8C] p-1">
                      <FilePdf size={15} weight="regular" />
                    </button>
                    <button data-testid={`template-download-png-${t.id}`} title="Download PNG" onClick={() => handleDownloadTemplate(t, "png")} className="text-[#6B7C85] hover:text-[#3A7A8C] p-1">
                      <FileImage size={15} weight="regular" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <FullPreviewModal open={!!preview} onOpenChange={(v) => !v && setPreview(null)} preview={preview} />

      <Dialog open={!!renameTarget} onOpenChange={(v) => !v && setRenameTarget(null)}>
        <DialogContent data-testid="template-rename-dialog" className="max-w-md">
          <DialogHeader><DialogTitle>Rename Template</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <Label>Template name</Label>
            <Input data-testid="template-rename-input" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} />
            <Button data-testid="template-rename-save-button" onClick={handleRenameSave} className="w-full bg-[#5FA8B4] text-white hover:bg-[#3A7A8C]">
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
